/**
 * src/lib/voice/knowledgePacket.js — Phase 90-01 Task 3
 *
 * Turns voice.chat from "answers with just project_name" into "answers with
 * active project/deal/vessel + top ChromaDB snippets + LightRAG neighbors +
 * top 15 vocab hints + recent conversation".
 *
 * Split: the client builds a BASE packet synchronously from AppDataContext
 * (no fetches, no server-only deps). The server receives the base via
 * voice.chat body and calls enrichKnowledgePacket() which runs the three
 * enrichment sources in parallel under a 500ms budget each
 * (Promise.allSettled — slow = dropped, never blocks the turn).
 *
 * Token budget: 1500 tokens using a 4-char heuristic. Over budget, the
 * truncator drops in this order: lightrag_neighbors → chromadb_hits tail →
 * vocab_hints tail → recent_tasks tail. active_project is ALWAYS kept.
 */

import { readProvenance, provenanceTag, resolveProvenanceNames } from '../../../lib/rag/provenance.mjs';

const DEFAULT_BUDGET = 1500;
const CHARS_PER_TOKEN = 4;

/** Crude token estimate. 4 chars ≈ 1 token is the GPT rule of thumb. */
export function estimateTokens(str = '') {
  if (!str) return 0;
  return Math.ceil(String(str).length / CHARS_PER_TOKEN);
}

/**
 * Phase 76-03 — map 'category' from domain_vocabulary to the entity_type used
 * by entityResolver's FIELD_RESOLVERS. Unknown categories resolve to null,
 * which leaves the resolver on its ilike fallback path.
 */
function categoryToEntityType(category) {
  switch (category) {
    case 'project': return 'project';
    case 'supplier': return 'supplier';
    case 'person': return null; // ambiguous — could be contact OR user_profile
    default: return null;
  }
}

/**
 * Build the lowercase-alias → { canonical, entity_type, entity_id } map from
 * vocab hints. Pure, serializable after calling `aliasMapToObject`.
 */
export function buildAliasMap(vocabHints) {
  const map = new Map();
  if (!Array.isArray(vocabHints)) return map;
  for (const hint of vocabHints) {
    const canonical = hint?.term ? String(hint.term).trim() : '';
    if (!canonical) continue;
    const entity_type = categoryToEntityType(hint?.category);
    const entity_id = hint?.entity_id || null;
    const misheardList = Array.isArray(hint?.misheard_as) ? hint.misheard_as : [];
    for (const raw of misheardList) {
      const alias = String(raw || '').trim().toLowerCase();
      if (!alias || alias === canonical.toLowerCase()) continue;
      if (!map.has(alias)) {
        map.set(alias, { canonical, entity_type, entity_id });
      }
    }
  }
  return map;
}

/** Serialize a Map to a plain object for JSON transport. */
export function aliasMapToObject(map) {
  if (!map || typeof map.entries !== 'function') return {};
  const out = {};
  for (const [k, v] of map.entries()) out[k] = v;
  return out;
}

/** Rehydrate a plain object back into a Map. */
export function objectToAliasMap(obj) {
  const map = new Map();
  if (!obj || typeof obj !== 'object') return map;
  for (const [k, v] of Object.entries(obj)) map.set(k, v);
  return map;
}

function withTimeout(promise, ms, fallback) {
  return new Promise((resolve) => {
    let done = false;
    const t0 = Date.now();
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      resolve({ value: fallback, timedOut: true, ms: Date.now() - t0 });
    }, ms);
    Promise.resolve(promise)
      .then((value) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve({ value, timedOut: false, ms: Date.now() - t0 });
      })
      .catch((err) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve({ value: fallback, timedOut: false, ms: Date.now() - t0, error: err });
      });
  });
}

/**
 * Build the BASE packet from already-loaded app state. Synchronous — safe
 * to call in React render and send over the wire as JSON.
 *
 * @param {object} inputs
 * @param {object|null} inputs.activeProject  - raw Supabase row (snake_case)
 * @param {object|null} inputs.activeDeal
 * @param {object|null} inputs.activeVessel
 * @param {Array}       inputs.recentTasks    - top-N by updated_at, caller trims
 * @param {Array}       inputs.conversationHistory - [{role, text|content}]
 * @param {string}      inputs.userLanguage   - 'en' | 'es'
 * @param {string}      inputs.responseMode   - 'voice_short' | 'text_full'
 * @param {object}      [inputs.clientLookup] - optional { clientId → client_name } for project.client_name
 */
export function buildBasePacket({
  activeProject = null,
  activeDeal = null,
  activeVessel = null,
  recentTasks = [],
  conversationHistory = [],
  userLanguage = 'en',
  responseMode = 'voice_short',
  clientLookup = null,
} = {}) {
  return {
    active_project: activeProject ? compactProject(activeProject, clientLookup) : null,
    active_deal: activeDeal ? compactDeal(activeDeal) : null,
    active_vessel: activeVessel ? compactVessel(activeVessel) : null,
    recent_tasks: (recentTasks || []).slice(0, 5).map(compactTask),
    conversation_history: (conversationHistory || []).slice(-4).map((t) => ({
      role: t.role,
      text: t.text || t.content || '',
    })),
    user_language: userLanguage || 'en',
    response_mode: responseMode || 'voice_short',
  };
}

function compactProject(p, clientLookup) {
  const clientName =
    p.client_name ||
    (clientLookup && p.client_id ? clientLookup[p.client_id] : null) ||
    null;
  return {
    id: p.id,
    name: p.project_name || p.name || '',
    client_name: clientName,
    stage: p.stage_name || p.stage || null,
    project_type: p.project_type || null,
  };
}
function compactDeal(d) {
  return {
    id: d.id,
    title: d.deal_name || d.title || '',
    value: d.deal_value || d.value || null,
    currency: d.currency || 'EUR',
    stage: d.stage_name || d.stage || null,
  };
}
function compactVessel(v) {
  return {
    id: v.id,
    name: v.vessel_name || v.name || '',
    make: v.make || null,
    model: v.model || null,
  };
}
function compactTask(t) {
  return {
    id: t.id,
    name: t.task_name || t.name || '',
    status: t.status || null,
    due: t.due_date || null,
  };
}

/**
 * Enrich a base packet with server-side RAG sources. Each source has its own
 * 500ms budget; slow/failing sources are dropped and logged but never block
 * the turn. Runs Promise.allSettled to guarantee return.
 *
 * @param {object} base   - from buildBasePacket()
 * @param {object} opts
 * @param {string} opts.lastUserText  - used as ChromaDB query
 * @param {object} [opts.deps]        - { vocabularyService, chromaRetrieve, lightragQuery, resolveNames, sb }
 *                  Each dep is optional. Missing dep = that source returns [].
 *                  resolveNames(provenances) -> { [id]: label } (TICKET-1261, one call per turn);
 *                  absent + sb (server Supabase client) -> resolveProvenanceNames(sb, ...).
 * @param {number} [opts.timeoutMs=500]
 * @param {number} [opts.maxTokens=1500]
 */
export async function enrichKnowledgePacket(base, {
  lastUserText = '',
  deps = {},
  timeoutMs = 500,
  maxTokens = DEFAULT_BUDGET,
} = {}) {
  const { vocabularyService, chromaRetrieve, lightragQuery, sb } = deps;
  const resolveNames = deps.resolveNames || (sb ? (provs) => resolveProvenanceNames(sb, provs) : null);
  const startedAt = Date.now();

  const vocabTask = vocabularyService
    ? withTimeout(
        vocabularyService.buildVocabHints({
          projectType: base.active_project?.project_type || null,
          vesselType: base.active_vessel?.model || null,
          language: base.user_language,
          limit: 15,
          // Phase 76-03 — the sync normalizer + entity-resolver alias pre-pass
          // both need the full misheard_as list, not just terms. buildVocabHints
          // already returns it in the Supabase fallback path; ensure ChromaDB
          // path consumers don't rely on missing misheard_as (tolerated below).
        }),
        timeoutMs,
        [],
      )
    : Promise.resolve({ value: [], timedOut: false });

  const chromaTask = chromaRetrieve && lastUserText
    ? withTimeout(chromaRetrieve(lastUserText, 5), timeoutMs, [])
    : Promise.resolve({ value: [], timedOut: false });

  const lightragTask = lightragQuery && (base.active_project?.name || lastUserText)
    ? withTimeout(lightragQuery(base.active_project?.name || lastUserText, 3), timeoutMs, [])
    : Promise.resolve({ value: [], timedOut: false });

  const [vocabRes, chromaRes, lightragRes] = await Promise.all([
    vocabTask, chromaTask, lightragTask,
  ]);

  const vocabHints = Array.isArray(vocabRes.value) ? vocabRes.value : [];
  const chromadbHits = Array.isArray(chromaRes.value) ? chromaRes.value : [];
  const lightragNeighbors = Array.isArray(lightragRes.value) ? lightragRes.value : [];

  // TICKET-1261 (R5): who/when/where per snippet. Names: one batched lookup, same budget.
  const hitProvs = chromadbHits.map((c) => (c && typeof c === 'object' ? c.provenance || readProvenance(c.metadata) : null));
  const namesRes = resolveNames && chromadbHits.length
    ? await withTimeout(Promise.resolve().then(() => resolveNames(hitProvs.filter(Boolean))).catch(() => { /* spine-emit-ok: names are decoration; the tag drops the name */ return {}; }), timeoutMs, {})
    : { value: {} };
  const names = namesRes.value || {};

  // Phase 76-03 — vocabulary alias map for sync transcript normalization
  // + entity-resolver alias pre-pass. Stored as a plain object on the packet
  // (JSON-safe). Consumers call `objectToAliasMap()` to rehydrate to a Map.
  const aliasMap = buildAliasMap(vocabHints);

  const packet = {
    ...base,
    vocab_hints: vocabHints.map((h) => ({ term: h.term, misheard_as: h.misheard_as || [] })),
    vocabulary_alias_map: aliasMapToObject(aliasMap),
    chromadb_hits: chromadbHits.map((c, i) => {
      const hit = {
        text: typeof c === 'string' ? c : (c.text || ''),
        source: c?.metadata?.source || c?.metadata?.doc_type || null,
      };
      const tag = provenanceTag(hitProvs[i], names);
      if (tag) hit.tag = tag;
      return hit;
    }),
    lightrag_neighbors: lightragNeighbors.slice(0, 3),
  };

  const { packet: finalPacket, truncated } = truncateToBudget(packet, maxTokens);

  // RAG telemetry (Tier 1) — capture per-candidate retrieval scores BEFORE the
  // packet shape drops them, so a later reranker/embedding swap can be measured
  // against a real baseline. truncateToBudget trims from the tail, so the first
  // `usedCount` candidates are the ones the model actually saw.
  const usedCount = finalPacket.chromadb_hits.length;
  const chromaCandidates = chromadbHits.map((c, i) => ({
    score: (c && typeof c === 'object' && c.distance != null) ? c.distance : null,
    source: c?.metadata?.source || c?.metadata?.doc_type || null,
    doc_type: c?.metadata?.doc_type || null,
    used: i < usedCount,
  }));

  return {
    packet: finalPacket,
    stats: {
      tokens: estimateTokens(serializeForBudget(finalPacket)),
      fetched_ms: {
        total: Date.now() - startedAt,
        vocab_ms: vocabRes.ms ?? null,
        chromadb_ms: chromaRes.ms ?? null,
        lightrag_ms: lightragRes.ms ?? null,
        vocab_timed_out: vocabRes.timedOut,
        chromadb_timed_out: chromaRes.timedOut,
        lightrag_timed_out: lightragRes.timedOut,
      },
      counts: {
        vocab_hints: finalPacket.vocab_hints.length,
        chromadb_hits: finalPacket.chromadb_hits.length,
        lightrag_neighbors: finalPacket.lightrag_neighbors.length,
        recent_tasks: finalPacket.recent_tasks.length,
      },
      retrieval: {
        candidates: chromaCandidates,
        retrieved_count: chromadbHits.length,
        used_count: usedCount,
        dropped_count: Math.max(0, chromadbHits.length - usedCount),
        zero_result: chromadbHits.length === 0
          && lightragNeighbors.length === 0
          && vocabHints.length === 0,
      },
      truncated,
    },
  };
}

/** Serialize only fields that count toward the budget — system prompt equivalent. */
function serializeForBudget(p) {
  const parts = [];
  if (p.active_project) parts.push(JSON.stringify(p.active_project));
  if (p.active_deal) parts.push(JSON.stringify(p.active_deal));
  if (p.active_vessel) parts.push(JSON.stringify(p.active_vessel));
  parts.push(JSON.stringify(p.recent_tasks || []));
  parts.push(JSON.stringify(p.conversation_history || []));
  parts.push(JSON.stringify(p.vocab_hints || []));
  parts.push(JSON.stringify(p.chromadb_hits || []));
  parts.push(JSON.stringify(p.lightrag_neighbors || []));
  return parts.join('\n');
}

/**
 * Drop sources until the serialized packet fits within maxTokens. Order:
 *   1. lightrag_neighbors (whole array)
 *   2. chromadb_hits (tail-first, keeps top-ranked)
 *   3. vocab_hints (tail-first)
 *   4. recent_tasks (tail-first)
 * active_project / active_deal / active_vessel / conversation_history are
 * always kept — they are the irreducible context of the turn.
 */
export function truncateToBudget(packet, maxTokens = DEFAULT_BUDGET) {
  const truncated = [];
  const work = {
    ...packet,
    recent_tasks: [...(packet.recent_tasks || [])],
    vocab_hints: [...(packet.vocab_hints || [])],
    chromadb_hits: [...(packet.chromadb_hits || [])],
    lightrag_neighbors: [...(packet.lightrag_neighbors || [])],
  };

  const overBudget = () => estimateTokens(serializeForBudget(work)) > maxTokens;

  if (overBudget() && work.lightrag_neighbors.length) {
    work.lightrag_neighbors = [];
    truncated.push('lightrag_neighbors');
  }
  while (overBudget() && work.chromadb_hits.length) {
    work.chromadb_hits.pop();
    if (!truncated.includes('chromadb_hits')) truncated.push('chromadb_hits');
  }
  while (overBudget() && work.vocab_hints.length) {
    work.vocab_hints.pop();
    if (!truncated.includes('vocab_hints')) truncated.push('vocab_hints');
  }
  while (overBudget() && work.recent_tasks.length) {
    work.recent_tasks.pop();
    if (!truncated.includes('recent_tasks')) truncated.push('recent_tasks');
  }

  return { packet: work, truncated };
}

/**
 * Convert the packet into the system-prompt block appended to voice.chat.
 * Empty sections are omitted entirely — we don't spend tokens on headers
 * with no data. Preserves VOICE RULES boilerplate upstream.
 */
export function renderPacketAsPrompt(packet) {
  if (!packet) return '';
  const lines = [];

  if (packet.active_project || packet.active_deal || packet.active_vessel) {
    lines.push('ACTIVE ENTITIES:');
    if (packet.active_project) {
      const bits = [`Project: ${packet.active_project.name}`];
      if (packet.active_project.client_name) bits.push(`client: ${packet.active_project.client_name}`);
      if (packet.active_project.stage) bits.push(`stage: ${packet.active_project.stage}`);
      lines.push('- ' + bits.join(' — '));
    }
    if (packet.active_deal) {
      const bits = [`Deal: ${packet.active_deal.title}`];
      if (packet.active_deal.value != null) bits.push(`${packet.active_deal.value} ${packet.active_deal.currency || ''}`.trim());
      if (packet.active_deal.stage) bits.push(`stage: ${packet.active_deal.stage}`);
      lines.push('- ' + bits.join(' — '));
    }
    if (packet.active_vessel) {
      const bits = [`Vessel: ${packet.active_vessel.name}`];
      if (packet.active_vessel.make || packet.active_vessel.model) {
        bits.push([packet.active_vessel.make, packet.active_vessel.model].filter(Boolean).join(' '));
      }
      lines.push('- ' + bits.join(' — '));
    }
  }

  if (packet.recent_tasks?.length) {
    lines.push('RECENT TASKS:');
    for (const t of packet.recent_tasks) {
      const bits = [t.name || 'Untitled'];
      if (t.status) bits.push(t.status);
      if (t.due) bits.push(`due ${t.due}`);
      lines.push(`- ${bits.join(' · ')}`);
    }
  }

  if (packet.conversation_history?.length) {
    lines.push('CONVERSATION:');
    for (const turn of packet.conversation_history) {
      const text = (turn.text || '').slice(0, 200);
      lines.push(`- ${turn.role}: ${text}`);
    }
  }

  if (packet.vocab_hints?.length) {
    const terms = packet.vocab_hints.map((h) => h.term).filter(Boolean);
    if (terms.length) lines.push(`VOCAB HINTS: ${terms.join(', ')}`);
  }

  if (packet.chromadb_hits?.length) {
    lines.push('KNOWLEDGE SNIPPETS:');
    for (const hit of packet.chromadb_hits) {
      const text = (hit.text || '').replace(/\s+/g, ' ').trim().slice(0, 240);
      if (text) lines.push(`- ${text}${hit.tag ? ` (${hit.tag})` : ''}`);
    }
  }

  if (packet.lightrag_neighbors?.length) {
    lines.push(`RELATED ENTITIES: ${packet.lightrag_neighbors.join(', ')}`);
  }

  if (packet.user_language) lines.push(`LANGUAGE: ${packet.user_language}`);
  if (packet.response_mode === 'voice_short') {
    lines.push('RESPONSE MODE: voice_short — 1-3 spoken sentences, no markdown, plain speech only.');
  } else if (packet.response_mode === 'voice_short_text_full') {
    lines.push('RESPONSE MODE: voice_short_text_full — first emit 1-2 concise spoken sentences (natural speech, no markdown), then the literal marker [[FULL]] on its own line (no code fences), then 3-6 additional sentences of written detail. Never speak or describe the marker.');
  }

  return lines.join('\n');
}
